/* eslint-disable import/prefer-default-export */

import React from 'react'
import { defaultHandleStatus } from '@src/endpoints/helpers'
import type { APIResponse, APIRequest } from '@src/endpoints'

interface Options {
  onCallbackError?: (data?: string) => void
  showToastMessage?: boolean
}

export interface RequestProps<T, V, R> {
  request: (args: APIRequest<T, V, R>) => Promise<APIResponse<R>>
  reqParams: APIRequest<T, V, R>
  options?: Options
}
/**
 * useApiRequest is one of the React hooks
 * @description It's intended to be used for cases when you have a component that has network dependencies (i.e. things need to load from the server in order for this component to render properly)
 * @param request function that make call to api (services)
 * @param reqParams Request parameters
 * @param options the hook options (optional)
 * @param deps If present, effect will only activate if the values in the list change.
 *
 */
export const useApiRequest = <T, V, R>(
  {
    request,
    reqParams,
    options: { onCallbackError, showToastMessage } = {
      showToastMessage: true
    }
  }: RequestProps<T, V, R>,
  deps: ReadonlyArray<any> = []
) => {
  const [response, setResponse] = React.useState<R>(null)
  const [error, setError] = React.useState(null)
  const [pending, setPending] = React.useState(false)

  function handleErrors(data) {
    setError(data)
    setPending(false)
    if (typeof onCallbackError === 'function') {
      onCallbackError(data)
    }
  }

  React.useLayoutEffect(() => {
    const fetch = async () => {
      try {
        setPending(true)
        request(reqParams)
          .then(res => defaultHandleStatus(res, handleErrors, showToastMessage))
          .then(res => {
            setResponse(res)
            setPending(false)
          })
          .catch(ex => {
            setError(error)
            setPending(false)
            return ex
          })
      } catch (error) {
        setError(error)
        setPending(false)
      }
    }
    if (reqParams?.params) {
      fetch()
    }
  }, deps)
  return { response, error, pending }
}
